import React, { useState, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import BusinessSource from 'components/BusinessSource'
import Icon from 'components/Icon'
import { CopyOutlined, CheckOutlined } from '@ant-design/icons'
import { camelCase } from 'lodash'
import cx from 'classnames'
import styles from './ContactCard.scss'
import messages from './messages'

export interface IWeblinks {
  [index: string]: Array<string>
}

export type IContactType = 'weblinks' | 'phones' | 'emails' | 'addresses' | 'wallets'

export interface IContactCard {
  type: IContactType
  emails: Array<string>
  phones: Array<string>
  websiteLinks: IWeblinks
  isNFT: boolean
  isInfluencers: boolean
  addresses?: Array<string>
  wallets?: Array<string>
}

/**
 * Format E.164 phone numbers for display.
 * +595971653648  → +595 971 653 648
 * +59521295018   → +595 21 295 018
 * +12024704654   → +1 (202) 470-4654
 */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '')

  // Paraguay +595
  if (digits.startsWith('595')) {
    const local = digits.slice(3)
    if (local.startsWith('9') && local.length === 9) {
      return `+595 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`
    }
    if (local.startsWith('21') && local.length >= 8) {
      return `+595 21 ${local.slice(2, 5)} ${local.slice(5)}`
    }
    if (local.length >= 8) {
      return `+595 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`
    }
    return `+595 ${local}`
  }

  // US/Canada +1
  if (digits.startsWith('1') && digits.length === 11) {
    const area = digits.slice(1, 4)
    const mid = digits.slice(4, 7)
    const last = digits.slice(7)
    return `+1 (${area}) ${mid}-${last}`
  }

  return raw
}

const DISPLAY_NAMES: Record<string, string> = {
  airbnb_profile: 'AirBnB Profile',
  airbnbProfile: 'AirBnB Profile',
  airbnb: 'AirBnB',
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'X (Twitter)',
  gmaps: 'Google Maps',
  googleMaps: 'Google Maps',
  bbb: 'BBB',
  angiesList: "Angie's List",
  angies: "Angie's List",
  other: 'Other',
}

const ICON_OVERRIDES: Record<string, string> = {
  airbnb_profile: 'airbnb',
  airbnbProfile: 'airbnb',
}

const COLLAPSED_ITEM_COUNT = 4

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    },
    [text],
  )

  return (
    <span
      className={cx(styles.copyBtn, { [styles.copied]: copied })}
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? <CheckOutlined /> : <CopyOutlined />}
    </span>
  )
}

export default function ContactCard({
  type,
  websiteLinks,
  phones,
  emails,
  isNFT,
  addresses,
  isInfluencers,
  wallets,
}: IContactCard) {
  const [expanded, setExpanded] = useState(false)

  const getDisplayName = (key: string) => DISPLAY_NAMES[key] || key
  const getIconName = (key: string) => ICON_OVERRIDES[key] || camelCase(key)

  const renderWebLinks = () => {
    const items: React.ReactNode[] = []
    Object.keys(websiteLinks).forEach(key => {
      if (!websiteLinks[key]) return
      const source = getDisplayName(key)
      websiteLinks[key].forEach((url, index) => {
        const valueName = websiteLinks[key].length === 1 ? source : `${source} ${index + 1}`
        const iconName = getIconName(key)
        items.push(
          <article key={`${key}-${index}`} className={styles.item}>
            <h5 className={styles.header}>
              <span className={styles.value}>
                <a href={url} target='_blank' rel='noreferrer'>
                  <span className={styles.name}>
                    <BusinessSource source={iconName} isHoverable isClickable />
                  </span>
                  {valueName}
                </a>
                <CopyButton text={url} />
              </span>
            </h5>
          </article>,
        )
      })
    })
    return items
  }

  const renderPhones = () =>
    phones.map((phone, i) => {
      const formatted = formatPhone(phone)
      return (
        <article key={`phone-${i}`} className={styles.item}>
          <h5 className={styles.header}>
            <span className={styles.value}>
              <a href={`tel:${phone}`}>{formatted}</a>
              <CopyButton text={formatted} />
            </span>
          </h5>
        </article>
      )
    })

  const renderEmails = () =>
    emails.map((email, i) => (
      <article key={`email-${i}`} className={styles.item}>
        <h5 className={styles.header}>
          <span className={cx(styles.value, styles.noCapitalize)}>
            <a href={`mailto:${email}`}>{email}</a>
            <CopyButton text={email} />
          </span>
        </h5>
      </article>
    ))

  const renderAddresses = () =>
    (addresses || []).map((addr, i) => (
      <article key={`addr-${i}`} className={styles.item}>
        <h5 className={styles.header}>
          <span className={styles.value}>
            {addr}
            <CopyButton text={addr} />
          </span>
        </h5>
      </article>
    ))

  const renderWallets = () =>
    (wallets || []).map((wallet, i) => (
      <article key={`wallet-${i}`} className={styles.item}>
        <h5 className={styles.header}>
          <span className={styles.value}>
            <span className={styles.name}>
              <Icon name='wallets' size='small' />
            </span>
            {wallet}
            <CopyButton text={wallet} />
          </span>
        </h5>
      </article>
    ))

  let items: React.ReactNode[] = []
  if (type === 'weblinks') items = renderWebLinks()
  if (type === 'phones') items = renderPhones()
  if (type === 'emails') items = renderEmails()
  if (type === 'wallets') items = renderWallets()
  if (isInfluencers && type === 'addresses') items = renderAddresses()

  const needsExpand = items.length > COLLAPSED_ITEM_COUNT
  const visibleItems = expanded ? items : items.slice(0, COLLAPSED_ITEM_COUNT)

  return (
    <div
      className={cx(styles.container, { [styles.nftContainer]: isNFT, [styles.influencersContainer]: isInfluencers })}
    >
      <div className={styles.title}>
        {(type !== 'addresses' || (type === 'addresses' && isInfluencers)) && (
          <Icon name={type} size='regular' className={styles.icon}>
            <FormattedMessage {...messages[type]} />
          </Icon>
        )}
      </div>
      <div className={styles.inner}>
        <div className={styles.content}>{visibleItems}</div>
        {needsExpand && (
          <div className={styles.expandControl} onClick={() => setExpanded(!expanded)}>
            {expanded ? '▲' : '···'}
          </div>
        )}
      </div>
    </div>
  )
}
